<template>
  <section :class="embedded ? 'py-0' : 'bg-[#fcf5ec] py-12'">
    <div :class="embedded ? 'p-0  mx-auto' : 'bg-[#EEDCC5] p-6 lg:p-12 mx-auto'">
      <h2 class="text-[38px] font-black italic uppercase text-[#111] mb-6">{{ t('productManage.title') }}</h2>

      <div v-if="!canManage" class="text-[#842029] bg-[#f8d7da] border border-[#f5c2c7] p-3 text-[14px]">{{ t('productManage.notAuthorizedNotice') || 'Je hebt geen rechten om materialen te beheren.' }}</div>

      <div v-else>
        <!-- Flash messages -->
        <div v-if="message" class="text-[#0f5132] bg-[#d1e7dd] border border-[#badbcc] px-3 py-2 text-[14px] mb-4">{{ message }}</div>
        <div v-if="error" class="text-[#842029] bg-[#f8d7da] border border-[#f5c2c7] px-3 py-2 text-[14px] mb-4">{{ error }}</div>

        <div class="bg-[#F6EBDC] p-5">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-black text-white grid place-items-center font-extrabold">1</div>
            <h3 class="text-[20px] font-black italic uppercase m-0">{{ t('productManage.overview') }}</h3>
          </div>

          <div v-if="loading" class="text-[15px] text-[#666]">{{ t('messages.loading') }}</div>
          <div v-else class="w-full">
            <div class="space-y-4 w-full">
              <!-- Topbar: search + sort -->
              <div class="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                <div v-if="selectedMaterialIds.length > 0" class="flex flex-wrap items-center gap-2">
                  <button type="button" @click="exportToCsv"
                          :disabled="exporting"
                          title="Selectie exporteren"
                          class="h-10 px-3 flex items-center justify-center gap-2 border border-black bg-black text-white hover:bg-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                    <span class="text-xs font-bold uppercase italic">{{ exporting ? t('messages.loading') : ('Selectie exporteren (' + selectedMaterialIds.length + ')') }}</span>
                  </button>
                  <button type="button" @click="exportAllToCsv"
                          :disabled="exporting"
                          title="Alle materialen exporteren"
                          class="h-10 px-3 flex items-center justify-center gap-2 border border-black bg-white text-black hover:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                    </svg>
                    <span class="text-xs font-bold uppercase italic">{{ exporting ? t('messages.loading') : 'Alles exporteren' }}</span>
                  </button>
                </div>
                <div class="flex-1">
                  <MmSearchInput v-model="query" :placeholder="t('productManage.searchPlaceholder')" @change="onSearch" />
                </div>
                <div class="flex items-center gap-2">
                  <button
                    type="button"
                    @click="filterType = ''; onSearch()"
                    class="px-4 h-10 border border-black text-xs font-bold uppercase italic transition-colors"
                    :class="!filterType ? 'bg-black text-white' : 'bg-white text-black hover:bg-neutral-50'"
                  >
                    {{ t('productManage.filter.all') || 'Alles' }}
                  </button>
                  <button
                    type="button"
                    @click="filterType = 'aangeboden'; onSearch()"
                    class="px-4 h-10 border border-black text-xs font-bold uppercase italic transition-colors"
                    :class="filterType === 'aangeboden' ? 'bg-[#458b45] text-white border-[#458b45]' : 'bg-white text-black hover:bg-neutral-50'"
                  >
                    {{ t('productManage.filter.offered') || 'Aangeboden' }}
                  </button>
                  <button
                    type="button"
                    @click="filterType = 'gevraagd'; onSearch()"
                    class="px-4 h-10 border border-black text-xs font-bold uppercase italic transition-colors"
                    :class="filterType === 'gevraagd' ? 'bg-mm-orange text-white border-mm-orange' : 'bg-white text-black hover:bg-neutral-50'"
                  >
                    {{ t('productManage.filter.requested') || 'Gevraagd' }}
                  </button>
                </div>
                <div class="flex items-center gap-2 text-[14px] relative">
                  <select v-model="sortKey" @change="onSearch"
                          class="h-10 px-3 pr-8 border border-black bg-white text-sm font-bold uppercase italic focus:ring-0 focus:border-black appearance-none min-w-[180px]">
                    <option value="date_desc">{{ t('productManage.sort.newest') }}</option>
                    <option value="date_asc">{{ t('productManage.sort.oldest') }}</option>
                    <option value="title_asc">{{ t('productManage.sort.titleAZ') }}</option>
                    <option value="title_desc">{{ t('productManage.sort.titleZA') }}</option>
                    <option value="status_asc">{{ t('productManage.sort.statusAZ') }}</option>
                    <option value="status_desc">{{ t('productManage.sort.statusZA') }}</option>
                  </select>
                  <svg class="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" fill="none"
                       stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
              </div>

              <div v-if="!items.length" class="text-[15px] text-[#444] py-8">
                {{ t('productManage.empty') }}
                <a href="/account/materialen-toevoegen" class="underline hover:no-underline font-semibold">{{ t('productManage.emptyAction') }}</a>
              </div>
              <div v-else>
                <!-- Table -->
                <MmTable
                :columns="columns"
                :rows="visibleRows"
                :sort-key="sortKeyBase"
                :sort-dir="sortDirBase"
                :selectable="true"
                :selected-rows="selectedMaterialIds"
                :all-selected="allPageSelected"
                @sort-change="toggleSort"
                @select-row="toggleMaterialSelection"
                @select-all="toggleAllPageSelection"
              >
                <template #cell:photo="{ row }">
                  <img :src="row.thumbnail || row.image || placeholderImgLocal" alt="" class="h-16 w-16 object-cover border border-neutral-300 bg-[#f9f4ec]"/>
                </template>
                <template #cell:title="{ row }">
                  <div class="flex flex-col">
                    <div class="font-semibold text-neutral-900 truncate">{{ row.title || t('productManage.placeholders.noTitle') }}</div>
                    <div class="flex gap-1 mt-1">
                      <span v-if="row.mm_type === 'gevraagd'" class="bg-mm-orange text-white px-2 py-0.5 text-[10px] font-black uppercase italic tracking-wider">
                        {{ t('productManage.filter.requested') || 'Gevraagd' }}
                      </span>
                      <span v-else class="bg-[#458b45] text-white px-2 py-0.5 text-[10px] font-black uppercase italic tracking-wider">
                        {{ t('productManage.filter.offered') || 'Aangeboden' }}
                      </span>
                    </div>
                  </div>
                </template>
                <template #cell:id="{ row }">
                  <span>{{ padId(row.id) }}</span>
                </template>
                <template #cell:status="{ row }">
                  <div class="flex items-center gap-1">
                    <span v-if="statusLabel(row)" :class="['inline-block rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-center', statusClass(row)]">{{ statusLabel(row) }}</span>
                  </div>
                </template>
                <template #cell:date="{ row }">{{ formatDate(row.date) }}</template>
                <template #cell:actions="{ row }">
                  <div class="flex items-center gap-2">
                    <button
                      type="button"
                      class="px-3 py-1 border border-black bg-white italic uppercase text-[12px] font-bold hover:bg-black hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      :disabled="isActivelyClaimed(row)"
                      :title="isActivelyClaimed(row) ? t('productManage.tooltips.editDisabled') : ''"
                      @click="onClickEdit(row)"
                    >{{ editingId===row.id ? t('productManage.buttons.cancel') : t('productManage.buttons.edit') }}</button>
                    <button
                      type="button"
                      class="px-3 py-1 border border-black bg-white italic uppercase text-[12px] font-bold hover:bg-black hover:text-white text-[#b91c1c] disabled:opacity-50 disabled:cursor-not-allowed"
                      :disabled="isActivelyClaimed(row)"
                      :title="isActivelyClaimed(row) ? t('productManage.tooltips.deleteDisabled') : ''"
                      @click="onClickDelete(row)"
                    >{{ t('productManage.buttons.delete') }}</button>
                  </div>
                </template>
              </MmTable>

              <!-- Pagination -->
              <MmPagination :page="clientPage" :pages="clientPages" @page-change="goTo" />
            </div>

            <!-- Edit form below table when selected -->
        <form v-if="editingItem" @submit.prevent="save(editingItem)" class="mt-4 border-t border-[#e6ded2] pt-4 max-w-[1060px]">
                <!-- Contextkop: welke materiaal bewerk je? -->
                <div class="mb-4">
                  <h4 class="text-[18px] font-semibold m-0">
                    {{ editingItem.title || t('productManage.placeholders.noTitle') }}
                  </h4>
                  <div class="text-[13px] text-neutral-700">
                    {{ t('productManage.labels.adId') }}: <span>{{ padId(editingItem.id) }}</span>
                  </div>
                </div>
                <div class="bg-[#EDDCC4] p-5">
                  <div class="mb-3 flex items-center gap-2">
                    <span class="text-[14px] text-[#333]">{{ t('productManage.labels.status') }}:</span>
                    <span v-if="statusLabel(editingItem)" :class="['inline-block rounded-sm px-2 py-1 text-[11px] font-extrabold uppercase tracking-widest', statusClass(editingItem)]">{{ statusLabel(editingItem) }}</span>
                  </div>
                  <div class="mb-4">
                    <div class="flex items-center gap-3 mb-3">
                      <div class="w-10 h-10 bg-black text-white flex-shrink-0 grid place-items-center font-extrabold">1</div>
                      <h3 class="text-[20px] font-black italic uppercase m-0">{{ t('productManage.labels.sections.base') }}</h3>
                    </div>
                    <div class="space-y-4">
                      <div class="flex items-center justify-between gap-2">
                        <label class="text-[14px] text-[#333] font-bold">{{ t('productCreate.labels.title') }} *</label>
                        <MmInfoTip :text="t('productCreate.tips.title')" />
                      </div>
                      <input v-model="edit.title" type="text" required :placeholder="t('productManage.placeholders.title')" class="h-12 px-3 border bg-white w-full border-[#d8d1c6] box-border" />
                      <div class="flex items-center justify-between gap-2">
                        <label class="text-[14px] text-[#333] font-bold">{{ t('productCreate.labels.description') }} *</label>
                        <MmInfoTip :text="t('productCreate.tips.description')" />
                      </div>
                      <textarea v-model="edit.content" rows="4" required :placeholder="t('productManage.placeholders.description')" class="px-3 py-2 border bg-white w-full border-[#d8d1c6] box-border"></textarea>
                    </div>
                  </div>
                  <div class="mb-4">
                    <div class="flex items-center gap-3 mb-3">
                      <div class="w-10 h-10 bg-black text-white flex-shrink-0 grid place-items-center font-extrabold">2</div>
                      <h3 class="text-[20px] font-black italic uppercase m-0">{{ t('productManage.labels.sections.properties') }}</h3>
                    </div>
                    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
                      <div>
                        <div class="flex items-center justify-between mb-1 gap-2">
                          <label class="text-[14px] text-[#333] font-bold">{{ t('materials.type') || 'Type' }} *</label>
                        </div>
                        <div class="relative">
                          <select v-model="edit.mm_type" required class="h-12 px-3 pr-8 border bg-white w-full border-[#d8d1c6] box-border appearance-none">
                            <option value="aangeboden">{{ t('materials.offered') || 'Aangeboden' }}</option>
                            <option value="gevraagd">{{ t('materials.requested') || 'Gevraagd' }}</option>
                          </select>
                          <svg class="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" fill="none"
                               stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </div>
                      </div>
                      <div v-if="edit.mm_type !== 'gevraagd'">
                        <div class="flex items-center justify-between mb-1 gap-2">
                          <label class="text-[14px] text-[#333] font-bold">{{ t('productCreate.labels.valueEUR') }} *</label>
                          <MmInfoTip :text="t('productCreate.tips.valueEUR')" />
                        </div>
                        <input v-model="edit.waarde" type="number" step="0.01" min="0" required :placeholder="t('productManage.placeholders.valueEUR')" class="h-12 px-3 border bg-white w-full border-[#d8d1c6] box-border" />
                      </div>
                      <div>
                        <div class="flex items-center justify-between mb-1 gap-2">
                          <label class="text-[14px] text-[#333] font-bold">{{ t('productCreate.labels.condition') }} *</label>
                          <MmInfoTip :text="t('productCreate.tips.status')" />
                        </div>
                        <div class="relative">
                          <select v-model="edit.status" required class="h-12 px-3 pr-8 border bg-white w-full border-[#d8d1c6] box-border appearance-none">
                            <option value="">{{ t('productCreate.placeholders.condition') }}</option>
                            <option value="nieuw">{{ t('productCreate.conditionOptions.nieuw') }}</option>
                            <option value="gebruikt">{{ t('productCreate.conditionOptions.gebruikt') }}</option>
                            <option value="defect">{{ t('productCreate.conditionOptions.defect') }}</option>
                          </select>
                          <svg class="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" fill="none"
                               stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </div>
                      </div>
                      <div>
                        <div class="flex items-center justify-between mb-1 gap-2">
                          <label class="text-[14px] text-[#333] font-bold">{{ t('productCreate.labels.dimensions') }}</label>
                          <MmInfoTip :text="t('productCreate.tips.dimensions')" />
                        </div>
                        <input v-model="edit.afmetingen" type="text" :placeholder="t('productManage.placeholders.dimensions')" class="h-12 px-3 border bg-white w-full border-[#d8d1c6] box-border" />
                      </div>
                      <div>
                        <div class="flex items-center justify-between mb-1 gap-2">
                          <label class="text-[14px] text-[#333] font-bold">{{ t('productCreate.labels.weight') }}</label>
                          <MmInfoTip :text="t('productCreate.tips.weight')" />
                        </div>
                        <input v-model="edit.gewicht" type="text" :placeholder="t('productManage.placeholders.weight')" class="h-12 px-3 border bg-white w-full border-[#d8d1c6] box-border" />
                      </div>
                      <div>
                        <div class="flex items-center justify-between mb-1 gap-2">
                          <label class="text-[14px] text-[#333] font-bold">{{ t('productCreate.labels.pickupLocation') }} *</label>
                          <MmInfoTip :text="t('productCreate.tips.pickupLocation')" />
                        </div>
                        <MmLocationAutocomplete
                          v-model="edit.locationData"
                          :placeholder="t('productManage.placeholders.location')"
                          :initialText="edit.locatie"
                          inputClass="h-12 px-3 border bg-white w-full border-[#d8d1c6] box-border"
                          :required="true"
                          @select="onEditLocationSelect"
                          @clear="onEditLocationClear"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="mb-4">
                    <div class="flex items-center gap-3 mb-3">
                      <div class="w-10 h-10 bg-black text-white flex-shrink-0 grid place-items-center font-extrabold">3</div>
                      <h3 class="text-[20px] font-black italic uppercase m-0">{{ t('productManage.labels.sections.categories') }} *</h3>
                    </div>
                    <div class="flex flex-wrap gap-2">
                      <label v-for="c in categories" :key="c.id" class="inline-flex items-center gap-2 text-[13px] px-2 py-1 bg-white border border-[#d8d1c6] cursor-pointer hover:border-black transition-colors">
                        <input type="checkbox" :value="c.id" v-model="edit.categories" class="w-4 h-4 accent-black">
                        <span>{{ c.name }}</span>
                      </label>
                    </div>
                  </div>
                  <!-- Afbeeldingen beheer -->
                  <div class="mb-4">
                    <div class="flex items-center gap-3 mb-3">
                      <div class="w-10 h-10 bg-black text-white flex-shrink-0 grid place-items-center font-extrabold">4</div>
                      <h3 class="text-[20px] font-black italic uppercase m-0">{{ t('productManage.labels.sections.image') }} *</h3>
                    </div>
                    
                    <div class="bg-white border border-[#d8d1c6] p-4 sm:p-6 space-y-6 overflow-hidden">
                      <!-- Bestaande & Nieuwe afbeeldingen gecombineerd in één grid -->
                      <div v-if="editingItem" class="space-y-4">
                        <div class="flex items-center justify-between gap-2">
                          <label class="block text-[14px] text-[#333] font-bold uppercase italic">
                            {{ t('productManage.labels.sections.image') }}
                          </label>
                          <MmInfoTip :text="t('productCreate.tips.gallery') || 'Voeg foto\'s toe om een goed beeld te geven van het materiaal. De eerste foto wordt de hoofdafbeelding.'" />
                        </div>
                        
                        <!-- Grid met afbeeldingen -->
                        <div v-if="editingItem.thumbnail || editingItem.image || (editingItem.gallery && editingItem.gallery.length) || editPreviews.length" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-4">
                          <!-- Hoofdafbeelding -->
                          <div v-if="editingItem.thumbnail || editingItem.image" class="relative aspect-square bg-[#f7f3ec] border border-[#e6ded2] overflow-hidden" :class="{'opacity-40': edit.deleteImage, 'border-black ring-1 ring-black': !edit.deleteImage}">
                            <img :src="editingItem.thumbnail || editingItem.image" class="object-cover w-full h-full cursor-zoom-in" @click="zoomImage = (editingItem.thumbnail || editingItem.image)" />
                            <div v-if="!edit.deleteImage" class="absolute top-0 left-0 bg-black text-white text-[9px] px-1.5 py-0.5 font-bold uppercase tracking-wider">
                              Hoofdfoto
                            </div>
                            <div v-if="edit.deleteImage" class="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <span class="bg-red-600 text-white text-[9px] px-1.5 py-0.5 font-bold uppercase">Verwijderen</span>
                            </div>
                            <div class="absolute top-1 right-1 flex gap-1">
                              <button v-if="!edit.deleteImage" type="button" @click="edit.deleteImage = true" class="bg-red-600 text-white p-1 shadow-md hover:bg-red-700" title="Verwijderen">
                                <svg class="w-3 h-3 sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                              </button>
                              <button v-else type="button" @click="edit.deleteImage = false" class="bg-blue-600 text-white p-1 shadow-md hover:bg-blue-700" title="Herstellen">
                                <svg class="w-3 h-3 sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="1 4 1 10 7 10"></polyline><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path></svg>
                              </button>
                            </div>
                          </div>

                          <!-- Gallery afbeeldingen -->
                          <div v-for="img in editingItem.gallery" :key="img.id" class="relative aspect-square bg-[#f7f3ec] border border-[#e6ded2] overflow-hidden" :class="{'opacity-40': edit.deleteGalleryIds.includes(img.id)}">
                            <img :src="img.thumb || img.url" class="object-cover w-full h-full cursor-zoom-in" @click="zoomImage = img.url" />
                            <div v-if="edit.deleteGalleryIds.includes(img.id)" class="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <span class="bg-red-600 text-white text-[9px] px-1.5 py-0.5 font-bold uppercase">Verwijderen</span>
                            </div>
                            <div class="absolute top-1 right-1 flex gap-1">
                              <button v-if="!edit.deleteGalleryIds.includes(img.id)" type="button" @click="toggleDeleteGallery(img.id)" class="bg-red-600 text-white p-1 shadow-md hover:bg-red-700" title="Verwijderen">
                                <svg class="w-3 h-3 sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                              </button>
                              <button v-else type="button" @click="toggleDeleteGallery(img.id)" class="bg-blue-600 text-white p-1 shadow-md hover:bg-blue-700" title="Herstellen">
                                <svg class="w-3 h-3 sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="1 4 1 10 7 10"></polyline><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path></svg>
                              </button>
                            </div>
                          </div>

                          <!-- Nieuwe previews -->
                          <div v-for="(p, idx) in editPreviews" :key="'new-'+idx" class="relative group aspect-square bg-[#f7f3ec] border border-[#e6ded2] overflow-hidden" :class="{'border-black ring-1 ring-black': !editingItem.thumbnail || edit.deleteImage ? idx === 0 : false}">
                            <img :src="p.url" class="object-cover w-full h-full cursor-zoom-in" @click="zoomImage = p.url" />
                            
                            <div class="absolute top-0 left-0 flex flex-col gap-1">
                              <div v-if="(!editingItem.thumbnail || edit.deleteImage) && idx === 0" class="bg-black text-white text-[9px] px-1.5 py-0.5 font-bold uppercase tracking-wider">
                                Nieuwe hoofdfoto
                              </div>
                              <div class="bg-blue-600 text-white text-[9px] px-1.5 py-0.5 font-bold uppercase tracking-wider">
                                Preview
                              </div>
                            </div>
                            <button type="button" @click="removeEditFile(idx)" class="absolute top-1 right-1 bg-red-600 text-white p-1 shadow-md hover:bg-red-700" title="Verwijderen">
                              <svg class="w-3 h-3 sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                            </button>
                          </div>
                        </div>

                        <!-- Placeholder als er geen afbeeldingen zijn -->
                        <div v-else class="py-12 border-2 border-dashed border-[#d8d1c6] flex flex-col items-center justify-center text-[#7a7366] bg-[#fcfaf7]">
                          <svg class="w-12 h-12 mb-2 opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                          <span class="text-[14px] italic">{{ t('productManage.labels.noImagesFound') || 'Geen afbeeldingen gevonden.' }}</span>
                        </div>
                      </div>

                      <!-- Upload sectie -->
                      <div class="mt-6 border-t border-[#d8d1c6] pt-6">
                        <div class="flex flex-col sm:flex-row sm:items-center gap-4">
                          <label for="edit-material-files" class="inline-flex items-center px-4 py-2 bg-black text-white text-[12px] sm:text-[14px] font-bold uppercase italic cursor-pointer hover:bg-gray-800 transition-colors shrink-0">
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path></svg>
                            {{ t('productManage.buttons.addImages') || 'Afbeeldingen toevoegen' }}
                          </label>
                          <input type="file" id="edit-material-files" multiple accept="image/jpeg,image/png,image/gif,image/webp" @change="onEditFiles" class="hidden" />
                          <span class="text-[12px] text-[#7a7366] italic leading-relaxed">{{ t('productManage.labels.image.recommended') }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                    <button type="submit" :disabled="saving" class="w-full sm:w-auto px-5 py-3 bg-black text-white uppercase italic font-semibold disabled:opacity-60">{{ saving ? t('productManage.buttons.saving') : (t('productManage.buttons.saveAndUpload') || 'Definitief opslaan en uploaden') }}</button>
                    <button type="button" class="w-full sm:w-auto px-5 py-3 border border-[#b8b0a3] bg-white italic uppercase hover:bg-[#f6efe4]" @click="cancelEdit">{{ t('productManage.buttons.cancel') }}</button>
                  </div>
                </div>
        </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Verwijder bevestigen modal -->
  <MmModal v-model="openDeleteModal" :title="t('productManage.confirm.deleteTitle')">
    <div class="space-y-4">
      <p>{{ t('productManage.confirm.deleteText') }}</p>
      <div v-if="deleteTarget" class="flex gap-4 items-start">
        <img :src="deleteTarget.thumbnail || placeholderImg" :alt="deleteTarget.title" class="w-20 h-20 object-cover border border-neutral-300 bg-[#f9f4ec]" />
        <div class="flex-1 min-w-0">
          <h3 class="font-semibold text-neutral-900 truncate">{{ deleteTarget.title || t('productManage.placeholders.noTitle') }}</h3>
          <!-- Geen aparte samenvatting meer tonen in verwijderdialoog -->
        </div>
      </div>
    </div>
    <template #footer>
      <button type="button" class="mm-modal__button" @click="openDeleteModal = false" :disabled="deleteLoading">{{ t('productManage.buttons.cancel') }}</button>
      <button type="button" @click="confirmDelete" :disabled="deleteLoading" class="px-4 py-2 rounded-md text-white" :class="deleteLoading ? 'bg-[#b91c1c]/70 cursor-wait' : 'bg-[#b91c1c]'"><span v-if="deleteLoading">{{ t('productManage.buttons.busy') }}</span><span v-else>{{ t('productManage.buttons.deleteConfirm') }}</span></button>
    </template>
  </MmModal>

  <!-- Zoom modal -->
  <MmModal v-model="zoomImage" :title="t('productManage.labels.image.zoomTitle') || 'Afbeelding vergroten'">
    <div class="flex items-center justify-center bg-black/5 p-2 rounded overflow-hidden min-h-[300px]">
      <img v-if="zoomImage" :src="zoomImage" alt="Zoom" class="max-w-full max-h-[70vh] object-contain shadow-lg" />
    </div>
    <template #footer>
      <button type="button" class="mm-modal__button" @click="zoomImage = null">{{ t('buttons.close') }}</button>
    </template>
  </MmModal>
</template>

<script>
import WpApiClient from '../lib/wp-api.js'
import MmModal from './MmModal.vue'
import MmTable from './MmTable.vue'
import MmSearchInput from './MmSearchInput.vue'
import MmPagination from './MmPagination.vue'
import MmInfoTip from './MmInfoTip.vue'
import { t } from '../i18n'
import { formatDateNL, padId as padIdUtil, decodeHTML } from '../lib/format-utils'
import MmLocationAutocomplete from './MmLocationAutocomplete.vue'

export default {
  name: 'MmProductManage',
  props: {
    embedded: { type: Boolean, default: false },
    initialMaterialId: { type: [Number, String], default: null },
  },
  components: { MmModal, MmTable, MmSearchInput, MmPagination, MmInfoTip, MmLocationAutocomplete },
  data(){
    return {
      // Expose translator to template context (Vue Options API templates access instance props)
      t,
      api: null,
      user: null,
      loading: true,
      exporting: false,
      items: [],
      selectedMaterialIds: [],
      // Server pagination wordt niet meer getoond; we halen maximaal 50 items op en pagineren client-side
      page: 1, // behouden voor backend call
      totalPages: 1, // niet gebruikt in UI
      error: '',
      message: '',
      editingId: null,
      filterType: '',
      edit: {
        title: '', content: '', waarde: '', afmetingen: '', gewicht: '', locatie: '', locationData: null, categories: [], 
        files: [], deleteImage: false, deleteGalleryIds: [], mm_type: 'aangeboden'
      },
      editPreviews: [],
      saving: false,
      categories: [],
      // zoom
      zoomImage: null,
      // delete modal
      openDeleteModal: false,
      deleteTarget: null,
      deleteLoading: false,
      placeholderImg: '',
      // Table controls (client-side)
      query: '',
      sortKey: 'date_desc',
      clientPage: 1,
      perPage: 10,
      placeholderImgLocal: 'data:image/svg+xml;utf8,' + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80"><rect width="80" height="80" fill="#f3f4f6"/></svg>`),
      columns: [
        { key: 'photo', label: t('productManage.columns.photo'), sortable: false },
        { key: 'title', label: 'Titel', sortable: true },
        { key: 'id', label: t('productManage.columns.adId'), sortable: true },
        { key: 'status', label: t('productManage.columns.status'), sortable: true },
        { key: 'date', label: t('productManage.columns.date'), sortable: true },
        { key: 'actions', label: t('productManage.columns.actions'), sortable: false },
      ],
    }
  },
  computed: {
    canManage(){ 
      const rt = ((this.user?.roleType)||'').toLowerCase()
      return rt === 'bedrijf' || rt === 'school'
    },
    isBedrijf(){ return ((this.user?.roleType)||'').toLowerCase()==='bedrijf' },
    sortKeyBase(){ return (String(this.sortKey).split('_')[0] || 'date') },
    sortDirBase(){ return (String(this.sortKey).split('_')[1] || 'desc') },
    filteredItems(){
      const q = (this.query || '').trim().toLowerCase()
      const type = this.filterType
      let items = this.items
      if (type) {
        items = items.filter(it => (it.mm_type || 'aangeboden') === type)
      }
      if (!q) return items
      return items.filter(it => {
        const t = String(it.title||'').toLowerCase()
        const d = String(it.description||it.content||'').toLowerCase()
        return t.includes(q) || d.includes(q)
      })
    },
    sortedItems(){
      const base = this.sortKeyBase
      const dir = this.sortDirBase
      const arr = [...this.filteredItems]
      arr.sort((a,b)=>{
        let av, bv
        if (base==='title') { av = String(a.title||'').toLowerCase(); bv = String(b.title||'').toLowerCase() }
        else if (base==='status') { av = String(this.statusLabel(a)||'').toLowerCase(); bv = String(this.statusLabel(b)||'').toLowerCase() }
        else { av = Date.parse(a.date||'')||0; bv = Date.parse(b.date||'')||0 }
        if (av===bv) return 0
        const res = av < bv ? -1 : 1
        return dir==='asc' ? res : -res
      })
      return arr
    },
    clientPages(){
      const total = Math.max(1, Math.ceil(this.sortedItems.length / this.perPage))
      return Array.from({ length: total }, (_, i) => i+1)
    },
    visibleRows(){
      const start = (this.clientPage - 1) * this.perPage
      return this.sortedItems.slice(start, start + this.perPage)
    },
    allPageSelected(){
      if (!this.visibleRows.length) return false
      return this.visibleRows.every(r => this.selectedMaterialIds.includes(r.id))
    },
    editingItem(){
      return this.items.find(it => it.id === this.editingId) || null
    },
  },
  watch: {
    initialMaterialId(id){
      this.openInitialMaterial(id)
    },
  },
  methods: {
    padId(val){ return padIdUtil(val) },
    normalizeMaterial(m){
      return {
        ...m,
        title: decodeHTML(m.title || m.name || ''),
        content: decodeHTML(m.content || ''),
        excerpt: decodeHTML(m.excerpt || '')
      }
    },
    toggleMaterialSelection(id, selected){
      if (!id) return
      if (selected) {
        if (!this.selectedMaterialIds.includes(id)) this.selectedMaterialIds.push(id)
      } else {
        this.selectedMaterialIds = this.selectedMaterialIds.filter(x => x !== id)
      }
    },
    toggleAllPageSelection(selected){
      if (selected) {
        this.visibleRows.forEach(r => {
          if (r?.id && !this.selectedMaterialIds.includes(r.id)) this.selectedMaterialIds.push(r.id)
        })
      } else {
        const pageIds = this.visibleRows.map(r => r?.id).filter(Boolean)
        this.selectedMaterialIds = this.selectedMaterialIds.filter(id => !pageIds.includes(id))
      }
    },
    joinList(value){
      if (Array.isArray(value)) return value.map(v => (v && typeof v === 'object') ? (v.name || v.slug || v.id || '') : v).filter(Boolean).join(', ')
      return value || ''
    },
    galleryUrls(value){
      if (!Array.isArray(value)) return ''
      return value.map(v => v?.url || v?.thumb || '').filter(Boolean).join(', ')
    },
    csvEscape(value){
      const s = String(value ?? '')
      return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    },
    downloadCsv(headers, rows, filenamePrefix){
      const csv = [headers, ...rows].map(row => row.map(this.csvEscape).join(';')).join('\n')
      const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const date = new Date().toISOString().slice(0, 10)
      a.href = url
      a.download = `${filenamePrefix}-${date}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    },
    materialTypeLabel(it){
      return (it?.mm_type || 'aangeboden') === 'gevraagd'
        ? (t('productManage.filter.requested') || 'Gevraagd')
        : (t('productManage.filter.offered') || 'Aangeboden')
    },
    exportRows(itemsToExport){
      return itemsToExport.map(it => [
        it.id || '',
        it.title || '',
        this.materialTypeLabel(it),
        this.statusLabel(it),
        it.status || '',
        it.conditie || it.mm_conditie || '',
        it.claim?.status || '',
        it.waarde || '',
        it.afmetingen || '',
        it.gewicht || '',
        it.locatie || '',
        this.joinList(it.categories),
        it.views ?? 0,
        it.date || '',
        it.excerpt || '',
        it.content || '',
        it.thumbnail || it.image || '',
        this.galleryUrls(it.gallery)
      ])
    },
    doExport(itemsToExport, filenamePrefix){
      if (!itemsToExport.length) return
      const headers = [
        'ID',
        'Titel',
        'Type',
        'Statuslabel',
        'Publicatiestatus',
        'Conditie',
        'Claimstatus',
        'Waarde',
        'Afmetingen',
        'Gewicht',
        'Locatie',
        'Categorieën',
        'Weergaven',
        'Datum',
        'Korte omschrijving',
        'Omschrijving',
        'Afbeelding URL',
        'Galerij URLs'
      ]
      this.downloadCsv(headers, this.exportRows(itemsToExport), filenamePrefix)
    },
    filterItemsForExport(items){
      const q = (this.query || '').trim().toLowerCase()
      const type = this.filterType
      let filtered = items
      if (type) filtered = filtered.filter(it => (it.mm_type || 'aangeboden') === type)
      if (!q) return filtered
      return filtered.filter(it => {
        const title = String(it.title || '').toLowerCase()
        const description = String(it.description || it.content || it.excerpt || '').toLowerCase()
        return title.includes(q) || description.includes(q)
      })
    },
    sortItemsForExport(items){
      const base = this.sortKeyBase
      const dir = this.sortDirBase
      const arr = [...items]
      arr.sort((a,b)=>{
        let av, bv
        if (base==='title') { av = String(a.title||'').toLowerCase(); bv = String(b.title||'').toLowerCase() }
        else if (base==='status') { av = String(this.statusLabel(a)||'').toLowerCase(); bv = String(this.statusLabel(b)||'').toLowerCase() }
        else { av = Date.parse(a.date||'')||0; bv = Date.parse(b.date||'')||0 }
        if (av===bv) return 0
        const res = av < bv ? -1 : 1
        return dir==='asc' ? res : -res
      })
      return arr
    },
    async fetchAllMaterialsForExport(){
      const perPage = 50
      const fetchPage = async (page) => this.api.listMyMaterials({
        page,
        perPage,
        type: this.filterType || '',
        nonce: window?.materiaalAjax?.nonce,
        _t: Date.now()
      })
      const first = await fetchPage(1)
      if (!first?.ok) throw new Error(first?.error || t('productManage.messages.loadError'))
      const all = Array.isArray(first.items) ? [...first.items] : []
      const totalPages = Number(first.totalPages) || 1
      for (let p = 2; p <= totalPages; p++) {
        const res = await fetchPage(p)
        if (!res?.ok) throw new Error(res?.error || t('productManage.messages.loadError'))
        if (Array.isArray(res.items)) all.push(...res.items)
      }
      return all.map(this.normalizeMaterial)
    },
    exportToCsv(){
      if (!this.selectedMaterialIds.length || this.exporting) return
      const selectedItems = this.sortItemsForExport(this.items.filter(it => this.selectedMaterialIds.includes(it.id)))
      this.doExport(selectedItems, 'materialen-export')
    },
    async exportAllToCsv(){
      if (this.exporting) return
      this.exporting = true
      this.error = ''
      try {
        const items = await this.fetchAllMaterialsForExport()
        this.doExport(this.sortItemsForExport(this.filterItemsForExport(items)), 'alle-materialen')
      } catch (e) {
        this.error = e?.message || 'Export mislukt'
      } finally {
        this.exporting = false
      }
    },
    isActivelyClaimed(it){
      const claimedBy = Number(it?.claim?.claimed_by_user||0)
      const claimStatus = String(it?.claim?.status||'').toLowerCase()
      return claimedBy > 0 && claimStatus !== 'cancelled'
    },
    onClickEdit(it){
      if (this.isActivelyClaimed(it)) {
        this.error = t('productManage.tooltips.editDisabled')
        return
      }
      this.toggleEdit(it)
    },
    onClickDelete(it){
      if (this.isActivelyClaimed(it)) {
        this.error = t('productManage.tooltips.deleteDisabled')
        return
      }
      this.openDelete(it)
    },
    async init(){
      this.api = new WpApiClient()
      try {
        let res = await this.api.getCurrentUser({ nonce: window?.materiaalAjax?.nonce })

        // Automatische retry bij nonce error
        if (res && !res.ok && (res.error === 'invalid_nonce' || (res.error && res.error.includes('nonce')))) {
          const newNonce = await this.api.refreshNonce(false);
          if (newNonce) {
            res = await this.api.getCurrentUser({ nonce: newNonce });
          }
        }

        this.user = res?.user || null
      } catch(_){ this.user = null }
      try {
        this.categories = await this.api.getCategories({ taxonomy: 'materiaal_categorie', perPage: 100 })
      } catch(_) { this.categories = [] }
      await this.load()
    },
    // Bepaal statuslabel volgens wens: Actief/Concept/Geclaimd/Archief
    statusLabel(it){
      const postStatus = String(it?.status||'').toLowerCase()
      const claimedBy = Number(it?.claim?.claimed_by_user||0)
      const claimStatus = String(it?.claim?.status||'').toLowerCase()
      if (postStatus === 'draft' || postStatus === 'pending') return 'Concept'
      if (postStatus === 'trash' || postStatus === 'archief' || postStatus === 'archive') return 'Archief'
      if (claimedBy && claimStatus !== 'cancelled') return 'Geclaimd'
      return 'Actief'
    },
    statusClass(it){
      const label = this.statusLabel(it)
      if (label === 'Geclaimd') return 'bg-[#3f873f] text-white'
      if (label === 'Concept') return 'bg-[#fff3cd] text-[#664d03] border border-[#ffecb5]'
      if (label === 'Archief') return 'bg-neutral-200 text-neutral-800'
      return 'bg-white text-black border border-black'
    },
      async load(){
      this.loading = true
      this.error = ''
      try{
        // Haal maximaal 50 items (serverlimiet) en pagineer client-side
        // Cache-busting met timestamp om te garanderen dat we de nieuwste data krijgen
        const targetId = this.initialMaterialTargetId()
        const res = await this.api.listMyMaterials({ 
          page: 1, 
          perPage: 50, 
          type: this.filterType || '',
          mid: targetId || undefined,
          nonce: window?.materiaalAjax?.nonce,
          _t: Date.now() 
        })
        if (res?.ok) {
          this.items = (res.items || []).map(this.normalizeMaterial)
          const itemIds = this.items.map(it => it.id)
          this.selectedMaterialIds = this.selectedMaterialIds.filter(id => itemIds.includes(id))
          this.clientPage = 1
          this.openInitialMaterial()
        } else {
          this.error = res?.error || t('productManage.messages.loadError')
        }
      } catch(e){ this.error = e?.message || String(e) }
      this.loading = false
    },
    // Server pagination UI is vervangen door client pagination
    changePage(p){ this.clientPage = p },
    goTo(p){ this.clientPage = p },
    onSearch(){ 
      this.clientPage = 1
      this.load()
    },
    toggleSort(key){
      const base = String(key)
      const currentBase = this.sortKeyBase
      const currentDir = this.sortDirBase
      if (base === currentBase) {
        this.sortKey = `${base}_${currentDir === 'asc' ? 'desc' : 'asc'}`
      } else {
        this.sortKey = `${base}_asc`
      }
      this.onSearch()
    },
    formatDate(iso){ return formatDateNL(iso) },
    initialMaterialTargetId(){
      const propId = Number(this.initialMaterialId || 0)
      if (propId > 0) return propId
      try {
        return Number(new URLSearchParams(window.location.search).get('mid') || 0)
      } catch(_) { return 0 }
    },
    openInitialMaterial(id = null){
      const targetId = Number(id || this.initialMaterialTargetId() || 0)
      if (!targetId || this.editingId === targetId) return
      const item = this.items.find(it => Number(it.id) === targetId)
      if (!item || this.isActivelyClaimed(item)) return
      this.toggleEdit(item)
    },
    toggleEdit(it){
      if (this.editingId===it.id) { this.cancelEdit(); return }
      this.editingId = it.id
      this.edit = {
        title: it.title || '',
        content: it.content || '',
        waarde: it.waarde || '',
        afmetingen: it.afmetingen || '',
        gewicht: it.gewicht || '',
        locatie: it.locatie || '',
        locationData: null,
        status: it.mm_conditie || it.conditie || '',
        mm_type: it.mm_type || 'aangeboden',
        categories: Array.isArray(it.categories) ? it.categories.map(c => c.id) : [],
        files: [],
        deleteImage: false,
        deleteGalleryIds: []
      }
      this.editPreviews = []
    },
    cancelEdit(){
      this.editPreviews.forEach(p => {
        if (p.url) try { URL.revokeObjectURL(p.url) } catch(_){}
      })
      this.editingId = null
      this.editPreviews = []
    },
    onEditFiles(e){
      const files = Array.from(e.target.files || [])
      if (!files.length) return

      files.forEach(f => {
        this.edit.files.push(f)
        try {
          this.editPreviews.push({
            url: URL.createObjectURL(f),
            name: f.name
          })
        } catch(err) {
          console.error("Preview error:", err)
        }
      })

      // Force reactivity
      this.edit.files = [...this.edit.files]
      this.editPreviews = [...this.editPreviews]

      e.target.value = ''
    },
    removeEditFile(idx){
      const p = this.editPreviews[idx]
      if (p && p.url) {
        try { URL.revokeObjectURL(p.url) } catch(_){}
      }
      this.edit.files.splice(idx, 1)
      this.editPreviews.splice(idx, 1)
    },
    onEditLocationSelect(item) {
      this.edit.locationData = item
      this.edit.locatie = item?.display_name || item?.place_name || ''
    },
    onEditLocationClear() {
      this.edit.locationData = null
      this.edit.locatie = ''
    },
    toggleDeleteGallery(id){
      const idx = this.edit.deleteGalleryIds.indexOf(id)
      if (idx > -1) {
        this.edit.deleteGalleryIds.splice(idx, 1)
      } else {
        this.edit.deleteGalleryIds.push(id)
      }
    },
    async save(it){
      if (!this.edit.title || !this.edit.content || !this.edit.status) { return }
      if (this.edit.mm_type !== 'gevraagd' && !this.edit.waarde) { return }
      const locatieStr = this.edit.locationData?.display_name || this.edit.locationData?.place_name || this.edit.locatie
      if (!locatieStr) { return }

      this.saving = true
      this.error = ''
      this.message = ''
      try{

        // Bepaal welke nieuwe bestanden naar imageFile gaan en welke naar galleryFiles
        // Als er geen bestaande thumbnail is OF deze wordt verwijderd, wordt het eerstvolgende beschikbare bestand de hoofdafbeelding.
        let imageFile = null
        let newThumbnailId = null
        let galleryFiles = [...this.edit.files]

        if (!it.thumbnail || this.edit.deleteImage) {
          // 1. Check of er een nieuw geüpload bestand is
          if (galleryFiles.length > 0) {
            imageFile = galleryFiles.shift()
          } 
          // 2. Zo niet, check of er een bestaande galerij-afbeelding is die NIET wordt verwijderd
          else if (it.gallery && it.gallery.length > 0) {
            const nextBest = it.gallery.find(g => !this.edit.deleteGalleryIds.includes(g.id))
            if (nextBest) {
              newThumbnailId = nextBest.id
            }
          }
        }

        const res = await this.api.updateMaterial({
          id: it.id,
          title: this.edit.title,
          content: this.edit.content,
          waarde: this.edit.waarde,
          afmetingen: this.edit.afmetingen,
          gewicht: this.edit.gewicht,
          locatie: this.edit.locationData?.display_name || this.edit.locationData?.place_name || this.edit.locatie,
          locationData: this.edit.locationData || null,
          status: this.edit.status,
          type: this.edit.mm_type,
          categories: this.edit.categories,
          imageFile: imageFile,
          galleryFiles: galleryFiles,
          deleteImage: this.edit.deleteImage,
          deleteGalleryIds: this.edit.deleteGalleryIds,
          newThumbnailId: newThumbnailId,
          nonce: window?.materiaalAjax?.nonce,
        })
        if (res?.ok) {
          this.message = t('productManage.messages.saved')
          this.cancelEdit()
          window.scrollTo({ top: 0, behavior: 'smooth' })
          // Verhoogde delay (1.5s) om WP database/cache/media de tijd te geven
          setTimeout(() => {
            this.load()
          }, 1500)
        } else {
          const code = res?.error
          if (code === 'missing_location') {
            this.error = 'Locatie is verplicht.'
          } else {
            this.error = code || t('productManage.messages.saveError')
          }
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
      } catch(e){ 
        this.error = e?.message || String(e)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
      this.saving = false
    },
    openDelete(it){
      this.deleteTarget = it
      this.openDeleteModal = true
      this.deleteLoading = false
    },
    async confirmDelete(){
      if (!this.deleteTarget) { this.openDeleteModal = false; return }
      this.deleteLoading = true
      this.error = ''
      this.message = ''
      try{
        const res = await this.api.deleteMaterial({ id: this.deleteTarget.id, nonce: window?.materiaalAjax?.nonce })
        if (res?.ok) {
          this.message = t('productManage.messages.deleted')
          this.openDeleteModal = false
          this.deleteTarget = null
          this.load()
        } else {
          this.error = res?.error || t('productManage.messages.deleteError')
        }
      } catch(e){ this.error = e?.message || String(e) }
      this.deleteLoading = false
    }
  },
  mounted(){ this.init() }
}
</script>

<style scoped>
</style>
